// Camera: which part of the map is on screen and how big (docs/07-ARCHITECTURE.md → Rendering).
// Pure functions so the math is unit-tested; the renderer owns the mutable camera.
import type { Bounds, Point } from './hexgeom';

export type Camera = {
  /** World point shown at the center of the viewport. */
  x: number;
  y: number;
  /** CSS pixels per world unit (hex circumradius). */
  scale: number;
};

export type Viewport = { w: number; h: number };
export type ZoomLimits = { min: number; max: number };

/** 1× zoom: hex circumradius in CSS px. A 28 px unit symbol (02-SYMBOLOGY.md §1) fits a hex. */
export const BASE_SCALE = 30;
/** Closest zoom: a hex about 240 px across. */
export const MAX_SCALE = 120;
/** Never zoom out past this (hexes would be specks). */
export const FLOOR_SCALE = 6;

export function worldToScreen(cam: Camera, vp: Viewport, p: Point): Point {
  return { x: (p.x - cam.x) * cam.scale + vp.w / 2, y: (p.y - cam.y) * cam.scale + vp.h / 2 };
}

export function screenToWorld(cam: Camera, vp: Viewport, s: Point): Point {
  return { x: (s.x - vp.w / 2) / cam.scale + cam.x, y: (s.y - vp.h / 2) / cam.scale + cam.y };
}

/** Scale at which the whole map fits inside the viewport minus padding (CSS px). */
export function fitScale(vp: Viewport, b: Bounds, pad: Insets = NO_INSETS): number {
  const w = Math.max(1, vp.w - pad.left - pad.right);
  const h = Math.max(1, vp.h - pad.top - pad.bottom);
  return Math.min(w / (b.maxX - b.minX), h / (b.maxY - b.minY));
}

export type Insets = { top: number; right: number; bottom: number; left: number };
export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

export function zoomLimits(vp: Viewport, b: Bounds, pad: Insets = NO_INSETS): ZoomLimits {
  const fit = fitScale(vp, b, pad);
  const min = Math.max(FLOOR_SCALE, Math.min(fit * 0.8, BASE_SCALE));
  return { min, max: Math.max(min, MAX_SCALE) };
}

/** Camera that shows the whole map, centered in the area left by the insets. */
export function fitCamera(vp: Viewport, b: Bounds, pad: Insets = NO_INSETS): Camera {
  const lim = zoomLimits(vp, b, pad);
  const scale = Math.min(lim.max, Math.max(lim.min, fitScale(vp, b, pad)));
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  // Shift so the map centers in the padded area, not the full viewport.
  const dx = (pad.right - pad.left) / 2 / scale;
  const dy = (pad.bottom - pad.top) / 2 / scale;
  return { x: cx + dx, y: cy + dy, scale };
}

/**
 * First view of a map. Like Fit, but when the screen is much taller than the map is wide
 * (portrait phone, landscape map), fill the height instead (up to 1×) and let the player pan.
 */
export function openingCamera(vp: Viewport, b: Bounds, pad: Insets = NO_INSETS): Camera {
  const fit = fitCamera(vp, b, pad);
  const h = Math.max(1, vp.h - pad.top - pad.bottom) / (b.maxY - b.minY);
  if (h < fit.scale * 1.3) return fit;
  const scale = Math.min(BASE_SCALE, h, zoomLimits(vp, b, pad).max);
  if (scale <= fit.scale) return fit;
  const dy = (pad.bottom - pad.top) / 2 / scale;
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 + dy, scale };
}

/** Keep the zoom within limits and the view center over the map. */
export function clampCamera(cam: Camera, b: Bounds, lim: ZoomLimits): Camera {
  const scale = Math.min(lim.max, Math.max(lim.min, cam.scale));
  return {
    x: Math.min(b.maxX, Math.max(b.minX, cam.x)),
    y: Math.min(b.maxY, Math.max(b.minY, cam.y)),
    scale,
  };
}

/** Move the map with the finger: a drag of (dx,dy) screen px. */
export function panBy(cam: Camera, dx: number, dy: number): Camera {
  return { x: cam.x - dx / cam.scale, y: cam.y - dy / cam.scale, scale: cam.scale };
}

/** Zoom by `factor` keeping the world point under screen point `s` fixed. */
export function zoomAt(
  cam: Camera,
  vp: Viewport,
  s: Point,
  factor: number,
  lim: ZoomLimits,
): Camera {
  const scale = Math.min(lim.max, Math.max(lim.min, cam.scale * factor));
  const before = screenToWorld(cam, vp, s);
  const next = { ...cam, scale };
  const after = screenToWorld(next, vp, s);
  return { x: next.x + before.x - after.x, y: next.y + before.y - after.y, scale };
}

// ---------- inertia ----------

/** Time constant of the glide after a fling, ms (close to iOS scroll views). */
export const FLING_TAU_MS = 325;
/** Below this speed (px/ms) the glide stops. */
export const FLING_STOP = 0.015;
/** Flings slower than this (px/ms) don't glide at all. */
export const FLING_START = 0.1;
/** Cap fling speed (px/ms) so a flick can't throw the map off the screen. */
export const FLING_MAX = 4;

export type Velocity = { vx: number; vy: number };

/** One glide step: how far to move this frame and the decayed velocity. */
export function flingStep(v: Velocity, dtMs: number): { dx: number; dy: number; v: Velocity } {
  const k = Math.exp(-dtMs / FLING_TAU_MS);
  // Integral of v·e^(-t/τ) over dt = v·τ·(1−k).
  const f = FLING_TAU_MS * (1 - k);
  return { dx: v.vx * f, dy: v.vy * f, v: { vx: v.vx * k, vy: v.vy * k } };
}

export function flingSpeed(v: Velocity): number {
  return Math.hypot(v.vx, v.vy);
}

export function capFling(v: Velocity): Velocity {
  const s = flingSpeed(v);
  if (s <= FLING_MAX) return v;
  return { vx: (v.vx / s) * FLING_MAX, vy: (v.vy / s) * FLING_MAX };
}

// ---------- zoom buckets for the terrain cache ----------

/** Terrain is cached at √2 steps of device-pixel scale; rounding up keeps it sharp. */
export function zoomBucket(devicePxPerUnit: number): number {
  const step = Math.ceil(Math.log2(Math.max(1e-6, devicePxPerUnit)) * 2 - 1e-9);
  return Math.pow(2, step / 2);
}
